import React, { useState, useEffect, useMemo } from 'react'
import { vendorWellService, wellService } from '../supabase'
import './ConstructionRecordForm.css'

const ConstructionRecordForm = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    well_id: '',
    selectedWellName: '',
    e_num: '',
    name: '',
    unit: '',
    hp: '',
    motor_type: '',
    motor_vendor: '',
    pump_segment_number: '',
    standard_hydraulic_head_x_standard_water_volume: '',
    'outlet_pipe_diameter(inch)': '',
    outlet_pipe_material: '',
    pump_depth: '',
    well_tube_material: '',
    'installation_depth_of_water_level_observation_pipe(m)': '',
    'static_water_level(m)': '',
    failure_reason: '',
    repair_date: '',
    vendor: ''
  })

  const [wells, setWells] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // 搜尋下拉選單相關狀態
  const [searchText, setSearchText] = useState('')
  const [showOptions, setShowOptions] = useState(false)
  const [filteredWells, setFilteredWells] = useState([])



  // 載入水井列表
  useEffect(() => {
    const fetchWells = async () => {
      try {
        const wellsData = await wellService.getWellsOnly()
        setWells(wellsData || [])
        setFilteredWells(wellsData || [])
      } catch (error) {
        console.error('載入水井列表失敗:', error)
      }
    }

    if (isOpen) {
      fetchWells()
    }
  }, [isOpen])

  // 搜尋過濾
  useEffect(() => {
    if (searchText.trim() === '') {
      setFilteredWells(wells)
    } else {
      const filtered = wells.filter(well =>
        well.name.toLowerCase().includes(searchText.toLowerCase()) ||
        (well.unit && well.unit.toLowerCase().includes(searchText.toLowerCase())) ||
        (well.e_num && well.e_num.toLowerCase().includes(searchText.toLowerCase()))
      )
      setFilteredWells(filtered)
    }
  }, [searchText, wells])

  const handleChange = (e) => {
    const { name, value } = e.target

    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  // 處理搜尋輸入
  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchText(value)
    setShowOptions(true)

    // 清空已選擇的水井
    setFormData(prev => ({
      ...prev,
      selectedWellName: '',
      well_id: ''
    }))
  }

  // 處理選擇水井
  const handleSelectWell = (well) => {
    setFormData(prev => ({
      ...prev,
      selectedWellName: well.name,
      well_id: well.id,
      unit: well.unit || '',
      name: well.name || '',
      e_num: well.e_num || ''
    }))
    setSearchText(well.name + (well.unit ? ` (${well.unit})` : ''))
    setShowOptions(false)
  }

  // 處理輸入框焦點
  const handleInputFocus = () => {
    setShowOptions(true)
  }

  // 處理輸入框失去焦點
  const handleInputBlur = () => {
    // 延遲關閉，讓用戶可以點擊選項
    setTimeout(() => {
      setShowOptions(false)
    }, 200)
  }



  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      // 檢查必填欄位
      if (!formData.selectedWellName || !formData.well_id) {
        setError('請選擇水井')
        setLoading(false)
        return
      }

      if (!formData.name) {
        setError('名稱為必填欄位')
        setLoading(false)
        return
      }

      // 準備提交的資料，排除 selectedWellName（這只是UI用的）
      const { selectedWellName, ...submitData } = formData

      // 提交資料到 Supabase
      const result = await vendorWellService.createConstructionRecord(submitData)

      // 清空表單
      setFormData({
        well_id: '',
        selectedWellName: '',
        e_num: '',
        name: '',
        unit: '',
        hp: '',
        motor_type: '',
        motor_vendor: '',
        pump_segment_number: '',
        standard_hydraulic_head_x_standard_water_volume: '',
        'outlet_pipe_diameter(inch)': '',
        outlet_pipe_material: '',
        pump_depth: '',
        well_tube_material: '',
        'installation_depth_of_water_level_observation_pipe(m)': '',
        'static_water_level(m)': '',
        failure_reason: '',
        repair_date: '',
        vendor: ''
      })

      // 清空搜尋相關狀態
      setSearchText('')
      setShowOptions(false)

      // 成功回調
      if (onSuccess) {
        onSuccess(result)
      }

      alert('施工紀錄新增成功！')
      onClose()

    } catch (error) {
      console.error('提交失敗:', error)
      setError('提交失敗：' + error.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>新增施工紀錄</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="construction-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-section">
            <h3>基本資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>選擇水井 <span style={{ color: '#e74c3c' }}>*</span></label>
                <div className="searchable-select">
                  <input
                    type="text"
                    value={searchText}
                    onChange={handleSearchChange}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    placeholder="搜尋水井名稱"
                    required={!formData.well_id}
                    autoComplete="off"
                  />
                  {showOptions && filteredWells.length > 0 && (
                    <div className="options-dropdown">
                      {filteredWells.map((well) => (
                        <div
                          key={well.id}
                          className="option-item"
                          onClick={() => handleSelectWell(well)}
                        >
                          <div className="well-name">{well.name}</div>
                          <div className="well-details">
                            {well.unit && <span>單位: {well.unit}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showOptions && filteredWells.length === 0 && searchText && (
                    <div className="options-dropdown">
                      <div className="no-options">沒有找到符合的水井</div>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>單位</label>
                <input
                  type="text"
                  name="unit"
                  value={formData.unit}
                  onChange={handleChange}
                  disabled
                  className="disabled-input"
                  required
                />
              </div>
              <div className="form-group">
                <label>電號</label>
                <input
                  type="text"
                  name="e_num"
                  value={formData.e_num}
                  onChange={handleChange}
                  disabled
                  className="disabled-input"
                />
              </div>



            </div>
          </div>

          <div className="form-section">
            <h3>馬達設備</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>馬力</label>
                <input
                  type="text"
                  name="hp"
                  value={formData.hp}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>馬達類型</label>
                <select
                  name="motor_type"
                  value={formData.motor_type}
                  onChange={handleChange}
                >
                  <option value="">請選擇馬達類型</option>
                  <option value="沉水式">沉水式</option>
                  <option value="立軸式">立軸式</option>
                  <option value="汙水">汙水</option>
                </select>
              </div>
              <div className="form-group">
                <label>馬達廠牌</label>
                <input
                  type="text"
                  name="motor_vendor"
                  value={formData.motor_vendor}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>馬達段數</label>
                <input
                  type="text"
                  name="pump_segment_number"
                  value={formData.pump_segment_number}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>標準揚程×標準水量</label>
                <input
                  type="text"
                  name="standard_hydraulic_head_x_standard_water_volume"
                  value={formData.standard_hydraulic_head_x_standard_water_volume}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>管線設備</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>出水管徑(吋)</label>
                <input
                  type="text"
                  name="outlet_pipe_diameter(inch)"
                  value={formData['outlet_pipe_diameter(inch)']}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>出水管材質</label>
                <input
                  type="text"
                  name="outlet_pipe_material"
                  value={formData.outlet_pipe_material}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>pump安裝深度(m)</label>
                <input
                  type="text"
                  name="pump_depth"
                  value={formData.pump_depth}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>井管材質</label>
                <input
                  type="text"
                  name="well_tube_material"
                  value={formData.well_tube_material}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>水位資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>水位觀測管安裝深度(m)</label>
                <input
                  type="text"
                  name="installation_depth_of_water_level_observation_pipe(m)"
                  value={formData['installation_depth_of_water_level_observation_pipe(m)']}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>靜水位(m)</label>
                <input
                  type="text"
                  name="static_water_level(m)"
                  value={formData['static_water_level(m)']}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>施工資訊</h3>
            <div className="form-grid">
              <div className="form-group">
                <label>故障原因 <span style={{ color: '#e74c3c' }}>*</span></label>
                <textarea
                  name="failure_reason"
                  value={formData.failure_reason}
                  onChange={handleChange}
                  rows="3"
                  required

                />
              </div>
              <div className="form-group">
                <label>修復日期</label>
                <input
                  type="date"
                  name="repair_date"
                  required
                  value={formData.repair_date}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>廠商名</label>
                <input
                  type="text"
                  name="vendor"
                  value={formData.vendor}
                  required
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="cancel-btn" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="submit-btn" disabled={loading}>
              {loading ? '提交中...' : '提交'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default ConstructionRecordForm 